import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CubeProvider, useCubeQuery } from "@cubejs-client/react";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { DateRange } from "react-day-picker";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Loader2, ArrowLeft, Calendar as CalendarIcon, Plus, Edit2, Trash2, Check, X, ChevronDown, ChevronUp, Download } from "lucide-react";
import { format } from "date-fns";
import cubeApi from "@/lib/cube";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import GridLayout, { WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ResponsiveGridLayout = WidthProvider(GridLayout);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const CHART_TYPES = [
  { value: "line", label: "Line Chart" },
  { value: "area", label: "Area Chart" },
  { value: "bar", label: "Bar Chart" },
  { value: "composed", label: "Composed Chart" },
  { value: "scatter", label: "Scatter Chart" },
  { value: "pie", label: "Pie Chart" },
  { value: "table", label: "Table" },
];

const TIME_GRANULARITIES = [
  { value: "hour", label: "Hour" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];

interface WidgetConfig {
  id?: number;
  i: string; // Unique identifier for grid layout
  title: string;
  chartType: string;
  measures: string[];
  dimensions: string[];
  filters?: Array<{
    member: string;
    operator: string;
    values: string[];
  }>;
  timeDimension?: {
    dimension: string;
    granularity: string;
    dateRange?: string;
    startDate?: Date;
    endDate?: Date;
  };
  x: number;
  y: number;
  w: number;
  h: number;
}

interface DashboardConfig {
  id?: number;
  name: string;
  description: string;
  widgets: WidgetConfig[];
  dateRangeType?: 'preset' | 'custom';
  dateRangePreset?: string;
  dateRangeStart?: Date;
  dateRangeEnd?: Date;
}

// Helper function to format measure names to friendly labels
const formatMeasureName = (measureName: string): string => {
  // Map common measure patterns to friendly names
  const measureMap: Record<string, string> = {
    'Orders.count': 'Total Orders',
    'Orders.totalAmount': 'Total Revenue',
    'Orders.totalamount': 'Total Revenue',
    'Orders.averageAmount': 'Average Order Value',
    'Orders.averageamount': 'Average Order Value',
    'Orders.maxAmount': 'Maximum Order',
    'Orders.maxamount': 'Maximum Order',
    'Orders.minAmount': 'Minimum Order',
    'Orders.minamount': 'Minimum Order',
  };

  // Return mapped name if exists, otherwise format generically
  if (measureMap[measureName]) {
    return measureMap[measureName];
  }

  // Generic formatting for unmapped measures
  if (typeof measureName === 'string' && measureName.includes('.')) {
    const parts = measureName.split('.');
    const measure = parts[parts.length - 1];
    return measure
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  return measureName;
};

// Helper function to format values to friendly names
const formatFriendlyName = (value: string | number): string => {
  if (typeof value !== 'string') return String(value);

  // Try to format as a measure name first
  const measureFormatted = formatMeasureName(value);
  if (measureFormatted !== value) {
    return measureFormatted;
  }

  return value
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to format numeric values (including currency)
const formatNumericValue = (value: any, measureName?: string): string => {
  // Convert string to number if needed
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (typeof numValue !== 'number' || isNaN(numValue)) {
    return String(value);
  }

  // Check if this is a currency measure
  const measureLower = measureName?.toLowerCase() || '';
  const isCurrency = measureName && (
    measureLower.includes('amount') ||
    measureLower.includes('revenue') ||
    measureLower.includes('price') ||
    measureLower.includes('cost') ||
    measureLower.includes('value') ||
    (measureLower.includes('total') && !measureLower.includes('count') && !measureLower.includes('orders'))
  );

  if (isCurrency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  }

  // For counts and other numbers, format with commas
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(numValue);
};

// Helper function to format legend labels based on granularity
const formatLegendValue = (value: string | number, granularity?: string): string => {
  if (typeof value !== 'string') return String(value);

  // Check if it looks like a timestamp (contains 't' or 'T' followed by time)
  if (value.match(/\d{4}-\d{2}-\d{2}[tT]\d{2}:\d{2}:\d{2}/)) {
    try {
      const date = new Date(value);

      // Format based on granularity
      if (granularity === 'year') {
        return date.getFullYear().toString();
      } else if (granularity === 'month') {
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      } else if (granularity === 'week') {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else {
        // day, hour, or default
        return date.toLocaleDateString();
      }
    } catch (e) {
      return value;
    }
  }

  return formatFriendlyName(value);
};

// Helper function to export data to CSV
const exportToCSV = (data: any[], filename: string) => {
  if (!data || data.length === 0) {
    return;
  }

  // Get all unique keys from the data
  const headers = Object.keys(data[0]);

  // Create CSV content
  const csvContent = [
    // Header row
    headers.join(','),
    // Data rows
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        // Handle values that might contain commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Widget Chart Component
const WidgetChart = ({
  widget,
  meta,
  dashboardStartDate,
  dashboardEndDate
}: {
  widget: WidgetConfig;
  meta: any;
  dashboardStartDate?: Date;
  dashboardEndDate?: Date;
}) => {
  // Build query config - useCubeQuery handles memoization internally via query serialization
  const queryConfig: any = {
    measures: widget.measures,
    dimensions: widget.dimensions,
  };

  if (widget.filters && widget.filters.length > 0) {
    queryConfig.filters = widget.filters;
  }

  if (widget.timeDimension) {
    queryConfig.timeDimensions = [{
      dimension: widget.timeDimension.dimension,
      granularity: widget.timeDimension.granularity,
      dateRange: dashboardStartDate && dashboardEndDate
        ? [
            format(dashboardStartDate, 'yyyy-MM-dd'),
            format(dashboardEndDate, 'yyyy-MM-dd'),
          ]
        : undefined,
    }];
  }

  console.log(`[WidgetChart] Query config for ${widget.title}:`, JSON.stringify(queryConfig, null, 2));

  const { resultSet, isLoading } = useCubeQuery(
    widget.measures.length > 0 ? queryConfig : null
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!resultSet || widget.measures.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Configure widget to see data
      </div>
    );
  }

  const data = resultSet.tablePivot();

  console.log(`[WidgetChart] ${widget.title}:`, {
    chartType: widget.chartType,
    measures: widget.measures,
    dimensions: widget.dimensions,
    dataLength: data.length,
    sampleData: data[0]
  });

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No data to display
      </div>
    );
  }

  const xAxisKey = widget.timeDimension
    ? `${widget.timeDimension.dimension}.${widget.timeDimension.granularity}`
    : widget.dimensions[0] || Object.keys(data[0])[0];

  // Calculate max value from data for explicit Y-axis domain
  const allValues = data.flatMap(item =>
    widget.measures.map(measure => Number(item[measure]) || 0)
  );
  const maxDataValue = Math.max(...allValues);
  const calculatedYMax = Math.ceil(maxDataValue * 1.5); // 50% padding above max value

  console.log(`[Chart ${widget.title}] Max value in data: ${maxDataValue}, Calculated Y-axis max: ${calculatedYMax}`);
  console.log(`[Chart ${widget.title}] All measure values:`, allValues);
  console.log(`[Chart ${widget.title}] Using STATIC domain: [0, ${calculatedYMax}]`);

  // Check if all values are zero or if there's no meaningful data
  if (maxDataValue === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="mb-2 opacity-50"
        >
          <line x1="12" y1="20" x2="12" y2="10"></line>
          <line x1="18" y1="20" x2="18" y2="4"></line>
          <line x1="6" y1="20" x2="6" y2="16"></line>
        </svg>
        <p className="text-sm font-medium">No data available</p>
        <p className="text-xs mt-1">Try adjusting the date range or filters</p>
      </div>
    );
  }

  // Helper to determine if we should show the legend
  // Hide legend when there are too many time-based entries to prioritize showing the graph
  const shouldShowLegend = () => {
    // For pie charts, always show legend as it's essential
    if (widget.chartType === 'pie') return true;

    // If we have non-time dimensions (like status, category), show legend as it's meaningful
    if (widget.dimensions.length > 0) return true;

    // For time-based data with multiple measures, show legend if useful
    if (widget.measures.length > 1 && widget.measures.length <= 5) return true;

    // For single measure time series, hide legend if too many data points
    if (data.length > 12) return false;

    return widget.measures.length > 1; // Show for multiple measures
  };

  const showLegend = shouldShowLegend();

  switch (widget.chartType) {
    case "line":
      // Check if we have non-time dimensions
      const hasNonTimeDimensionsLine = widget.dimensions.length > 0;

      if (hasNonTimeDimensionsLine) {
        // When we have dimensions, transform the data
        const dimensionKey = widget.dimensions[0];
        const dimensionValues = Array.from(new Set(data.map(item => item[dimensionKey]))).filter(Boolean);
        const transformedDataLine: any[] = [];
        const timeValues = Array.from(new Set(data.map(item => item[xAxisKey])));

        timeValues.forEach(timeValue => {
          const row: any = { [xAxisKey]: timeValue };
          dimensionValues.forEach(dimValue => {
            const matchingRows = data.filter(
              item => item[xAxisKey] === timeValue && item[dimensionKey] === dimValue
            );
            widget.measures.forEach(measure => {
              const key = `${dimValue}_${measure}`;
              row[key] = matchingRows.reduce((sum, r) => sum + (Number(r[measure]) || 0), 0);
            });
          });
          transformedDataLine.push(row);
        });

        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={transformedDataLine} margin={{ bottom: 60, left: 10, right: 10, top: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey={xAxisKey}
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
                interval="preserveStartEnd"
                tickFormatter={(value) =>
                  widget.timeDimension
                    ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : formatFriendlyName(value)
                }
              />
              <YAxis
                tick={{ fontSize: 11 }}
                domain={[0, calculatedYMax]}
              />
              <Tooltip
                formatter={(value: any, name: any) => {
                  const parts = name.split('_');
                  const dimValue = parts[0];
                  const measure = parts.slice(1).join('_');
                  return [formatNumericValue(value, measure), `${formatFriendlyName(dimValue)} - ${formatMeasureName(measure)}`];
                }}
                labelFormatter={(value) => {
                  if (widget.timeDimension && value) {
                    return new Date(value).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                  }
                  return formatFriendlyName(value);
                }}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '8px 12px'
                }}
              />
              {showLegend && (
                <Legend
                  formatter={(value) => {
                    const parts = value.split('_');
                    const dimValue = parts[0];
                    const measure = parts.slice(1).join('_');
                    return `${formatFriendlyName(dimValue)} - ${formatMeasureName(measure)}`;
                  }}
                  wrapperStyle={{ paddingTop: '10px' }}
                />
              )}
              {dimensionValues.flatMap((dimValue, dimIndex) =>
                widget.measures.map((measure) => (
                  <Line
                    key={`${dimValue}_${measure}`}
                    type="monotone"
                    dataKey={`${dimValue}_${measure}`}
                    stroke={COLORS[dimIndex % COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                ))
              )}
            </LineChart>
          </ResponsiveContainer>
        );
      }

      // Original logic when no dimensions
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ bottom: 60, left: 10, right: 10, top: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={xAxisKey}
              tick={{ fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={80}
              interval="preserveStartEnd"
              tickFormatter={(value) =>
                widget.timeDimension
                  ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : formatFriendlyName(value)
              }
            />
            <YAxis
              tick={{ fontSize: 11 }}
              domain={[0, calculatedYMax]}
            />
            <Tooltip
              formatter={(value: any, name: any) => [formatNumericValue(value, name), formatFriendlyName(name)]}
              labelFormatter={(value) => {
                if (widget.timeDimension && value) {
                  return new Date(value).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });
                }
                return formatFriendlyName(value);
              }}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '8px 12px'
              }}
            />
            {showLegend && (
              <Legend formatter={(value) => formatLegendValue(value, widget.timeDimension?.granularity)} wrapperStyle={{ paddingTop: '10px' }} />
            )}
            {widget.measures.map((measure, index) => (
              <Line
                key={measure}
                type="monotone"
                dataKey={measure}
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );

    case "area":
      // Check if we have non-time dimensions
      const hasNonTimeDimensionsArea = widget.dimensions.length > 0;

      if (hasNonTimeDimensionsArea) {
        // When we have dimensions, transform the data
        const dimensionKey = widget.dimensions[0];
        const dimensionValues = Array.from(new Set(data.map(item => item[dimensionKey]))).filter(Boolean);
        const transformedDataArea: any[] = [];
        const timeValues = Array.from(new Set(data.map(item => item[xAxisKey])));

        timeValues.forEach(timeValue => {
          const row: any = { [xAxisKey]: timeValue };
          dimensionValues.forEach(dimValue => {
            const matchingRows = data.filter(
              item => item[xAxisKey] === timeValue && item[dimensionKey] === dimValue
            );
            widget.measures.forEach(measure => {
              const key = `${dimValue}_${measure}`;
              row[key] = matchingRows.reduce((sum, r) => sum + (Number(r[measure]) || 0), 0);
            });
          });
          transformedDataArea.push(row);
        });

        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={transformedDataArea} margin={{ bottom: 60, left: 10, right: 10, top: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey={xAxisKey}
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
                interval="preserveStartEnd"
                tickFormatter={(value) =>
                  widget.timeDimension
                    ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : formatFriendlyName(value)
                }
              />
              <YAxis
                tick={{ fontSize: 11 }}
                domain={[0, calculatedYMax]}
              />
              <Tooltip
                formatter={(value: any, name: any) => {
                  const parts = name.split('_');
                  const dimValue = parts[0];
                  const measure = parts.slice(1).join('_');
                  return [formatNumericValue(value, measure), `${formatFriendlyName(dimValue)} - ${formatMeasureName(measure)}`];
                }}
                labelFormatter={(value) => {
                  if (widget.timeDimension && value) {
                    return new Date(value).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                  }
                  return formatFriendlyName(value);
                }}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '8px 12px'
                }}
              />
              {showLegend && (
                <Legend
                  formatter={(value) => {
                    const parts = value.split('_');
                    const dimValue = parts[0];
                    const measure = parts.slice(1).join('_');
                    return `${formatFriendlyName(dimValue)} - ${formatMeasureName(measure)}`;
                  }}
                  wrapperStyle={{ paddingTop: '10px' }}
                />
              )}
              {dimensionValues.flatMap((dimValue, dimIndex) =>
                widget.measures.map((measure) => (
                  <Area
                    key={`${dimValue}_${measure}`}
                    type="monotone"
                    dataKey={`${dimValue}_${measure}`}
                    fill={COLORS[dimIndex % COLORS.length]}
                    stroke={COLORS[dimIndex % COLORS.length]}
                    fillOpacity={0.6}
                    stackId="1"
                  />
                ))
              )}
            </AreaChart>
          </ResponsiveContainer>
        );
      }

      // Original logic when no dimensions
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ bottom: 60, left: 10, right: 10, top: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={xAxisKey}
              tick={{ fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={80}
              interval="preserveStartEnd"
              tickFormatter={(value) =>
                widget.timeDimension
                  ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : formatFriendlyName(value)
              }
            />
            <YAxis
              tick={{ fontSize: 11 }}
              domain={[0, calculatedYMax]}
            />
            <Tooltip
              formatter={(value: any, name: any) => [formatNumericValue(value, name), formatFriendlyName(name)]}
              labelFormatter={(value) => {
                if (widget.timeDimension && value) {
                  return new Date(value).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });
                }
                return formatFriendlyName(value);
              }}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '8px 12px'
              }}
            />
            {showLegend && (
              <Legend formatter={(value) => formatLegendValue(value, widget.timeDimension?.granularity)} wrapperStyle={{ paddingTop: '10px' }} />
            )}
            {widget.measures.map((measure, index) => (
              <Area
                key={measure}
                type="monotone"
                dataKey={measure}
                fill={COLORS[index % COLORS.length]}
                stroke={COLORS[index % COLORS.length]}
                fillOpacity={0.6}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      );

    case "bar":
      // Check if we have non-time dimensions
      const hasNonTimeDimensions = widget.dimensions.length > 0;

      if (hasNonTimeDimensions) {
        // When we have dimensions, we need to transform the data
        // Group by the x-axis (time) and create separate series for each dimension value
        const dimensionKey = widget.dimensions[0]; // Use first dimension for grouping

        // Get unique dimension values
        const dimensionValues = Array.from(new Set(data.map(item => item[dimensionKey]))).filter(Boolean);

        // Transform data: group by time period
        const transformedData: any[] = [];
        const timeValues = Array.from(new Set(data.map(item => item[xAxisKey])));

        timeValues.forEach(timeValue => {
          const row: any = { [xAxisKey]: timeValue };

          // For each dimension value, sum up the measures
          dimensionValues.forEach(dimValue => {
            const matchingRows = data.filter(
              item => item[xAxisKey] === timeValue && item[dimensionKey] === dimValue
            );

            widget.measures.forEach(measure => {
              const key = `${dimValue}_${measure}`;
              row[key] = matchingRows.reduce((sum, r) => sum + (Number(r[measure]) || 0), 0);
            });
          });

          transformedData.push(row);
        });

        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={transformedData} margin={{ bottom: 60, left: 10, right: 10, top: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey={xAxisKey}
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
                interval="preserveStartEnd"
                tickFormatter={(value) =>
                  widget.timeDimension
                    ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : formatFriendlyName(value)
                }
              />
              <YAxis
                tick={{ fontSize: 11 }}
                domain={[0, calculatedYMax]}
              />
              <Tooltip
                formatter={(value: any, name: any) => {
                  // Parse the name to extract dimension value and measure
                  const parts = name.split('_');
                  const dimValue = parts[0];
                  const measure = parts.slice(1).join('_');
                  return [formatNumericValue(value, measure), `${formatFriendlyName(dimValue)} - ${formatMeasureName(measure)}`];
                }}
                labelFormatter={(value) => {
                  if (widget.timeDimension && value) {
                    return new Date(value).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                  }
                  return formatFriendlyName(value);
                }}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '8px 12px'
                }}
              />
              {showLegend && (
                <Legend
                  formatter={(value) => {
                    const parts = value.split('_');
                    const dimValue = parts[0];
                    const measure = parts.slice(1).join('_');
                    return `${formatFriendlyName(dimValue)} - ${formatMeasureName(measure)}`;
                  }}
                  wrapperStyle={{ paddingTop: '10px' }}
                />
              )}
              {/* Create a Bar for each dimension value and measure combination */}
              {dimensionValues.flatMap((dimValue, dimIndex) =>
                widget.measures.map((measure, measureIndex) => (
                  <Bar
                    key={`${dimValue}_${measure}`}
                    dataKey={`${dimValue}_${measure}`}
                    fill={COLORS[dimIndex % COLORS.length]}
                    radius={[4, 4, 0, 0]}
                    stackId={measureIndex.toString()}
                  />
                ))
              )}
            </BarChart>
          </ResponsiveContainer>
        );
      }

      // Original logic when no dimensions - color by time/row
      const barLegendPayload = data.map((item, index) => ({
        value: formatLegendValue(item[xAxisKey]),
        type: 'square' as const,
        color: COLORS[index % COLORS.length]
      }));

      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ bottom: 60, left: 10, right: 10, top: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={xAxisKey}
              tick={{ fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={80}
              interval="preserveStartEnd"
              tickFormatter={(value) =>
                widget.timeDimension
                  ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : formatFriendlyName(value)
              }
            />
            <YAxis
              tick={{ fontSize: 11 }}
              domain={[0, calculatedYMax]}
            />
            <Tooltip
              formatter={(value: any, name: any) => [formatNumericValue(value, name), formatFriendlyName(name)]}
              labelFormatter={(value) => {
                if (widget.timeDimension && value) {
                  return new Date(value).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });
                }
                return formatFriendlyName(value);
              }}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '8px 12px'
              }}
            />
            {showLegend && (
              <Legend payload={barLegendPayload} wrapperStyle={{ paddingTop: '10px' }} />
            )}
            {widget.measures.map((measure, measureIndex) => (
              <Bar
                key={measure}
                dataKey={measure}
                fill={COLORS[measureIndex % COLORS.length]}
                radius={[4, 4, 0, 0]}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      );

    case "composed":
      return (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ bottom: 60, left: 10, right: 10, top: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={xAxisKey}
              tick={{ fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={80}
              interval="preserveStartEnd"
              tickFormatter={(value) =>
                widget.timeDimension
                  ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : value
              }
            />
            <YAxis
              tick={{ fontSize: 11 }}
              domain={[0, calculatedYMax]}
            />
            <Tooltip
              labelFormatter={(value) => {
                if (widget.timeDimension && value) {
                  return new Date(value).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });
                }
                return formatFriendlyName(value);
              }}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '8px 12px'
              }}
            />
            {showLegend && (
              <Legend formatter={(value) => formatLegendValue(value, widget.timeDimension?.granularity)} wrapperStyle={{ paddingTop: '10px' }} />
            )}
            {widget.measures.map((measure, index) => {
              return index % 2 === 0 ? (
                <Bar
                  key={measure}
                  dataKey={measure}
                  fill={COLORS[index % COLORS.length]}
                  radius={[4, 4, 0, 0]}
                />
              ) : (
                <Line
                  key={measure}
                  type="monotone"
                  dataKey={measure}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      );

    case "scatter":
      return (
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ bottom: 60, left: 10, right: 10, top: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={xAxisKey}
              tick={{ fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={80}
              interval="preserveStartEnd"
              tickFormatter={(value) =>
                widget.timeDimension
                  ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : value
              }
            />
            <YAxis
              tick={{ fontSize: 11 }}
              domain={[0, calculatedYMax]}
            />
            <Tooltip
              labelFormatter={(value) => {
                if (widget.timeDimension && value) {
                  return new Date(value).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });
                }
                return formatFriendlyName(value);
              }}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '8px 12px'
              }}
            />
            {showLegend && (
              <Legend formatter={(value) => formatLegendValue(value, widget.timeDimension?.granularity)} wrapperStyle={{ paddingTop: '10px' }} />
            )}
            {widget.measures.map((measure, index) => (
              <Scatter
                key={measure}
                name={measure}
                data={data}
                fill={COLORS[index % COLORS.length]}
                dataKey={measure}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      );

    case "pie":
      // Check if we have non-time dimensions
      const hasNonTimeDimensionsPie = widget.dimensions.length > 0;

      let pieData: { name: string; value: number }[] = [];

      if (hasNonTimeDimensionsPie) {
        // When we have dimensions, aggregate by dimension value across all time periods
        const dimensionKey = widget.dimensions[0];
        const measure = widget.measures[0];

        // Group by dimension value and sum the measure
        const aggregated = new Map<string, number>();

        data.forEach(item => {
          const dimValue = item[dimensionKey];
          const measureValue = Number(item[measure]) || 0;

          if (dimValue) {
            const current = aggregated.get(dimValue) || 0;
            aggregated.set(dimValue, current + measureValue);
          }
        });

        // Convert to pie data format
        pieData = Array.from(aggregated.entries()).map(([name, value]) => ({
          name: formatFriendlyName(name),
          value
        }));
      } else {
        // Original logic - use x-axis key (usually time)
        pieData = data.map((item) => ({
          name: formatLegendValue(item[xAxisKey]),
          value: item[widget.measures[0]],
        }));
      }

      if (!pieData || pieData.length === 0 || pieData.every(d => !d.value)) {
        return (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            No data available for pie chart
          </div>
        );
      }

      // Custom label rendering to avoid overlap - only show labels for slices > 8%
      const renderPieLabel = ({ name, percent, cx, cy, midAngle, innerRadius, outerRadius }: any) => {
        const RADIAN = Math.PI / 180;
        const percentValue = (percent * 100).toFixed(0);

        // Only show label if slice is large enough (> 8%)
        if (percent < 0.08) return null;

        // Calculate label position
        const radius = outerRadius * 1.15; // Position outside the pie
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
          <text
            x={x}
            y={y}
            fill="#374151"
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
            fontSize="12"
            fontWeight="500"
          >
            {`${name}: ${percentValue}%`}
          </text>
        );
      };

      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={{ stroke: '#e5e7eb', strokeWidth: 1 }}
              label={renderPieLabel}
              outerRadius="65%"
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip formatter={(value: any, name: any) => [formatNumericValue(value, widget.measures[0]), formatFriendlyName(name)]} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );

    case "table":
      return (
        <div className="overflow-auto h-full">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                {Object.keys(data[0]).map((key) => (
                  <th key={key} className="px-4 py-2 text-left font-medium">
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index} className="border-b">
                  {Object.values(row).map((value, i) => (
                    <td key={i} className="px-4 py-2">
                      {typeof value === "number"
                        ? value.toLocaleString()
                        : String(value)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      return null;
  }
};

const DashboardBuilderContent = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();

  const [meta, setMeta] = useState<any>(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [dashboardLoading, setDashboardLoading] = useState(!!id);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Dashboard config state - must be declared before callbacks that use it
  const [config, setConfig] = useState<DashboardConfig>({
    name: "Untitled Dashboard",
    description: "",
    widgets: [],
  });

  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editingWidget, setEditingWidget] = useState<WidgetConfig | null>(null);
  const [isWidgetSheetOpen, setIsWidgetSheetOpen] = useState(false);

  console.log('[DashboardBuilder] Render state:', { id, metaLoading, dashboardLoading, hasMeta: !!meta, hasError: !!error });

  // Auto-save function for widget positions (debounced)
  const autoSaveWidgetPositions = useCallback(async (widgets: WidgetConfig[]) => {
    if (!config.id || widgets.length === 0) {
      console.log('[DashboardBuilder] Auto-save skipped - no config.id or no widgets');
      return;
    }

    console.log('[DashboardBuilder] Auto-saving widget positions...', {
      dashboardId: config.id,
      widgetCount: widgets.length,
      widgets: widgets.map(w => ({ id: w.id, title: w.title, x: w.x, y: w.y, w: w.w, h: w.h }))
    });

    try {
      // Update all widgets in a batch
      const updates = widgets.map(widget => {
        if (!widget.id) {
          console.warn('[DashboardBuilder] Skipping widget without id:', widget.title);
          return null;
        }

        return supabase
          .from('dashboard_widgets')
          .update({
            size: JSON.stringify({
              x: widget.x,
              y: widget.y,
              w: widget.w,
              h: widget.h
            }),
          })
          .eq('id', widget.id);
      }).filter(Boolean);

      const results = await Promise.all(updates);

      // Check for errors in results
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        console.error('[DashboardBuilder] Auto-save had errors:', errors);
      } else {
        console.log('[DashboardBuilder] Auto-save completed successfully');
      }
    } catch (error) {
      console.error('[DashboardBuilder] Auto-save failed:', error);
    }
  }, [config.id]);

  // Auto-save function for full widget configuration (immediate)
  const autoSaveWidgetConfig = useCallback(async (widget: WidgetConfig) => {
    if (!widget.id || !config.id) return;

    console.log('[DashboardBuilder] Auto-saving widget configuration...', widget.title);

    try {
      const { error } = await supabase
        .from('dashboard_widgets')
        .update({
          title: widget.title,
          chart_type: widget.chartType,
          measures: widget.measures,
          dimensions: widget.dimensions,
          filters: widget.filters || null,
          time_dimension: widget.timeDimension,
          size: JSON.stringify({
            x: widget.x,
            y: widget.y,
            w: widget.w,
            h: widget.h
          }),
        })
        .eq('id', widget.id);

      if (error) {
        console.error('[DashboardBuilder] Auto-save widget config failed:', error);
      } else {
        console.log('[DashboardBuilder] Widget configuration auto-saved');
      }
    } catch (error) {
      console.error('[DashboardBuilder] Auto-save widget config exception:', error);
    }
  }, [config.id]);

  // Load metadata
  useEffect(() => {
    const loadMeta = async () => {
      try {
        console.log('[DashboardBuilder] Loading metadata...');
        setMetaLoading(true);
        const metadata = await cubeApi.meta();
        console.log('[DashboardBuilder] Metadata loaded:', metadata);
        setMeta(metadata);
        setError(null);
      } catch (err) {
        console.error("[DashboardBuilder] Failed to load metadata:", err);
        setError(err);
      } finally {
        setMetaLoading(false);
        console.log('[DashboardBuilder] Metadata loading complete');
      }
    };

    loadMeta();
  }, []);

  // Load existing dashboard if editing
  useEffect(() => {
    const loadDashboard = async () => {
      if (!id) {
        console.log('[DashboardBuilder] No ID, skipping dashboard load');
        return;
      }

      try {
        console.log('[DashboardBuilder] Loading dashboard with ID:', id);
        setDashboardLoading(true);

        // Load dashboard
        const { data: dashboardData, error: dashboardError } = await supabase
          .from('analytics_dashboards')
          .select('*')
          .eq('id', id)
          .single();

        if (dashboardError) throw dashboardError;
        console.log('[DashboardBuilder] Dashboard data loaded:', dashboardData);

        // Load widgets
        const { data: widgetsData, error: widgetsError } = await supabase
          .from('dashboard_widgets')
          .select('*')
          .eq('dashboard_id', id)
          .order('position');

        if (widgetsError) throw widgetsError;
        console.log('[DashboardBuilder] Widgets data loaded:', widgetsData);

        // Transform widgets data
        const widgets: WidgetConfig[] = (widgetsData || []).map((w, index) => {
          const sizeData = typeof w.size === 'string' ? JSON.parse(w.size) : { x: 0, y: index * 5, w: 6, h: 4 };
          console.log(`[DashboardBuilder] Widget ${w.id} (${w.title}) size data:`, {
            rawSize: w.size,
            parsedSizeData: sizeData,
            index
          });
          const measures = Array.isArray(w.measures) ? w.measures : [];

          // Auto-populate time dimension if missing
          let timeDimension = w.time_dimension;
          if (!timeDimension && measures.length > 0 && meta?.cubes) {
            const cubeName = measures[0].split('.')[0];
            const cube = meta.cubes.find((c) => c.name === cubeName);
            if (cube) {
              const timeField = cube.dimensions.find((d) =>
                d.type === 'time' &&
                (d.name.toLowerCase().includes('createdat') ||
                 d.name.toLowerCase().includes('created_at') ||
                 d.name.toLowerCase().includes('created'))
              );
              if (timeField) {
                timeDimension = {
                  dimension: timeField.name,
                  granularity: 'month'
                };
              }
            }
          }

          const finalWidget = {
            id: w.id,
            i: `widget-${w.id}`,
            title: w.title || '',
            chartType: w.chart_type,
            measures: measures,
            dimensions: Array.isArray(w.dimensions) ? w.dimensions : [],
            filters: Array.isArray(w.filters) ? w.filters : undefined,
            timeDimension: timeDimension,
            x: sizeData.x ?? 0,
            y: sizeData.y ?? (index * 5),
            w: sizeData.w ?? 6,
            h: sizeData.h ?? 4,
          };

          console.log(`[DashboardBuilder] Widget ${w.id} (${w.title}) final position:`, {
            x: finalWidget.x,
            y: finalWidget.y,
            w: finalWidget.w,
            h: finalWidget.h
          });

          return finalWidget;
        });

        console.log('[DashboardBuilder] Transformed widgets:', widgets);

        // Load date range settings if they exist
        console.log('[DashboardBuilder] Loading date range settings from dashboard data:', {
          hasDateRangeSettings: !!dashboardData.date_range_settings,
          dateRangeSettings: dashboardData.date_range_settings
        });

        let dateRangeType: 'preset' | 'custom' = 'preset';
        let dateRangePreset = 'last_30_days';
        let loadedStartDate = dashboardStartDate;
        let loadedEndDate = dashboardEndDate;

        if (dashboardData.date_range_settings) {
          const settings = typeof dashboardData.date_range_settings === 'string'
            ? JSON.parse(dashboardData.date_range_settings)
            : dashboardData.date_range_settings;

          console.log('[DashboardBuilder] Parsed date range settings:', settings);

          dateRangeType = settings.type || 'preset';
          dateRangePreset = settings.preset || 'last_30_days';

          if (settings.startDate) {
            loadedStartDate = new Date(settings.startDate);
          }
          if (settings.endDate) {
            loadedEndDate = new Date(settings.endDate);
          }

          console.log('[DashboardBuilder] Applied date range:', {
            type: dateRangeType,
            preset: dateRangePreset,
            startDate: loadedStartDate,
            endDate: loadedEndDate
          });
        } else {
          console.log('[DashboardBuilder] No date range settings found, using defaults');
        }

        setConfig({
          id: dashboardData.id,
          name: dashboardData.name,
          description: dashboardData.description || '',
          widgets,
          dateRangeType,
          dateRangePreset,
          dateRangeStart: loadedStartDate,
          dateRangeEnd: loadedEndDate,
        });

        // Apply the loaded date range
        console.log('[DashboardBuilder] Setting dashboard date range state:', {
          startDate: loadedStartDate,
          endDate: loadedEndDate,
          preset: dateRangePreset
        });
        setDashboardStartDate(loadedStartDate);
        setDashboardEndDate(loadedEndDate);
        setSelectedPreset(dateRangePreset);
      } catch (err) {
        console.error("[DashboardBuilder] Error loading dashboard:", err);
        toast({
          title: "Error",
          description: "Failed to load dashboard",
          variant: "destructive",
        });
      } finally {
        setDashboardLoading(false);
        console.log('[DashboardBuilder] Dashboard loading complete');
      }
    };

    loadDashboard();
  }, [id, toast]);

  // Dashboard-level date range state
  const [dashboardStartDate, setDashboardStartDate] = useState<Date | undefined>(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [dashboardEndDate, setDashboardEndDate] = useState<Date | undefined>(new Date());
  const [selectedPreset, setSelectedPreset] = useState<string>("last_30_days");

  // Date range presets
  const applyDatePreset = (preset: string) => {
    const today = new Date();
    let startDate: Date;
    let endDate: Date = new Date();

    switch (preset) {
      case "last_30_days":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 30);
        break;
      case "last_60_days":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 60);
        break;
      case "last_90_days":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 90);
        break;
      case "last_180_days":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 180);
        break;
      case "this_year":
        startDate = new Date(today.getFullYear(), 0, 1);
        break;
      case "last_year":
        startDate = new Date(today.getFullYear() - 1, 0, 1);
        endDate = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case "custom":
        setSelectedPreset("custom");
        return;
      default:
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 30);
    }

    console.log('[applyDatePreset]', { preset, startDate, endDate });
    setDashboardStartDate(startDate);
    setDashboardEndDate(endDate);
    setSelectedPreset(preset);

    // Auto-save the date range selection with the new values
    setTimeout(() => {
      saveDashboardMetadata(true, {
        preset,
        startDate,
        endDate
      });
    }, 500);
  };

  // Auto-save custom date range changes
  useEffect(() => {
    if (selectedPreset === 'custom' && config.id && dashboardStartDate && dashboardEndDate) {
      const timer = setTimeout(() => {
        saveDashboardMetadata(true, {
          preset: selectedPreset,
          startDate: dashboardStartDate,
          endDate: dashboardEndDate
        });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [dashboardStartDate, dashboardEndDate, selectedPreset, config.id]);

  // Cleanup auto-save timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Memoize layout to prevent unnecessary re-renders
  const layout = useMemo(() => config.widgets.map(w => ({
    i: w.i,
    x: w.x,
    y: w.y,
    w: w.w,
    h: w.h,
    minW: 2,
    minH: 2,
    maxW: 24,
    maxH: 20,
  })), [config.widgets]);

  // Helper function to find the next available position for a new widget
  const getNextAvailablePosition = (width: number = 12, height: number = 4) => {
    if (config.widgets.length === 0) {
      return { x: 0, y: 0 };
    }

    // Find the maximum y position + height to place new widget at the bottom
    const maxY = Math.max(
      ...config.widgets.map(w => (w.y || 0) + (w.h || 4)),
      0
    );

    // Place new widget at the start of the next row
    return { x: 0, y: maxY };
  };

  const handleAddWidget = () => {
    const defaultWidth = 12;
    const defaultHeight = 8;  // Increased from 4 to 8 for better chart visibility
    const position = getNextAvailablePosition(defaultWidth, defaultHeight);

    const newWidget: WidgetConfig = {
      i: `widget-${Date.now()}`,
      title: "New Widget",
      chartType: "bar",
      measures: [],
      dimensions: [],
      x: position.x,
      y: position.y,
      w: defaultWidth,
      h: defaultHeight,
    };
    setEditingWidget(newWidget);
    setIsWidgetSheetOpen(true);
  };

  const handleEditWidget = (widget: WidgetConfig) => {
    setEditingWidget(widget);
    setIsWidgetSheetOpen(true);
  };

  const handleSaveWidget = async () => {
    if (!editingWidget) return;

    if (editingWidget.measures.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one measure for the widget",
        variant: "destructive",
      });
      return;
    }

    try {
      const existingIndex = config.widgets.findIndex(w => w.i === editingWidget.i);
      const isUpdate = existingIndex !== -1 && editingWidget.id;

      if (isUpdate) {
        // Update existing widget in database
        const { error } = await supabase
          .from('dashboard_widgets')
          .update({
            title: editingWidget.title,
            chart_type: editingWidget.chartType,
            measures: editingWidget.measures,
            dimensions: editingWidget.dimensions,
            filters: editingWidget.filters || null,
            time_dimension: editingWidget.timeDimension,
            position: existingIndex,
            size: JSON.stringify({
              x: editingWidget.x,
              y: editingWidget.y,
              w: editingWidget.w,
              h: editingWidget.h
            }),
          })
          .eq('id', editingWidget.id);

        if (error) {
          console.error("Widget update error:", error);
          toast({
            title: "Error",
            description: `Failed to update widget: ${error.message}`,
            variant: "destructive",
          });
          return;
        }

        // Update local state
        const updatedWidgets = [...config.widgets];
        updatedWidgets[existingIndex] = editingWidget;
        setConfig(prev => ({ ...prev, widgets: updatedWidgets }));

        toast({
          title: "Success",
          description: "Widget updated",
        });
      } else {
        // Insert new widget into database
        if (!config.id) {
          toast({
            title: "Error",
            description: "Dashboard must be saved first",
            variant: "destructive",
          });
          return;
        }

        const { data: newWidget, error } = await supabase
          .from('dashboard_widgets')
          .insert({
            dashboard_id: config.id,
            title: editingWidget.title,
            chart_type: editingWidget.chartType,
            measures: editingWidget.measures,
            dimensions: editingWidget.dimensions,
            filters: editingWidget.filters || null,
            time_dimension: editingWidget.timeDimension,
            position: config.widgets.length,
            size: JSON.stringify({
              x: editingWidget.x,
              y: editingWidget.y,
              w: editingWidget.w,
              h: editingWidget.h
            }),
          })
          .select()
          .single();

        if (error) {
          console.error("Widget creation error:", error);
          toast({
            title: "Error",
            description: `Failed to create widget: ${error.message}`,
            variant: "destructive",
          });
          return;
        }

        // Update local state with the new widget including its database ID
        const widgetWithId = {
          ...editingWidget,
          id: newWidget.id,
        };

        setConfig(prev => ({
          ...prev,
          widgets: [...prev.widgets, widgetWithId]
        }));

        toast({
          title: "Success",
          description: "Widget added to dashboard",
        });
      }

      setIsWidgetSheetOpen(false);
      setEditingWidget(null);
    } catch (error) {
      console.error("Unexpected error saving widget:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleDeleteWidget = async (widgetId: string) => {
    try {
      // Find the widget to get its database ID
      const widget = config.widgets.find(w => w.i === widgetId);

      if (widget?.id) {
        // Delete from database if it has a database ID
        const { error } = await supabase
          .from('dashboard_widgets')
          .delete()
          .eq('id', widget.id);

        if (error) {
          console.error("Widget deletion error:", error);
          toast({
            title: "Error",
            description: `Failed to delete widget: ${error.message}`,
            variant: "destructive",
          });
          return;
        }
      }

      // Remove from local state
      setConfig(prev => ({
        ...prev,
        widgets: prev.widgets.filter(w => w.i !== widgetId)
      }));

      setIsWidgetSheetOpen(false);
      setEditingWidget(null);

      toast({
        title: "Success",
        description: "Widget deleted",
      });
    } catch (error) {
      console.error("Unexpected error deleting widget:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleExportWidget = async (widget: WidgetConfig) => {
    try {
      // Build the same query config as in WidgetChart
      const queryConfig: any = {
        measures: widget.measures,
        dimensions: widget.dimensions,
      };

      if (widget.filters && widget.filters.length > 0) {
        queryConfig.filters = widget.filters;
      }

      if (widget.timeDimension) {
        queryConfig.timeDimensions = [{
          dimension: widget.timeDimension.dimension,
          granularity: widget.timeDimension.granularity,
          dateRange: dashboardStartDate && dashboardEndDate
            ? [
                format(dashboardStartDate, 'yyyy-MM-dd'),
                format(dashboardEndDate, 'yyyy-MM-dd'),
              ]
            : undefined,
        }];
      }

      // Fetch the data
      const resultSet = await cubeApi.load(queryConfig);
      const data = resultSet.tablePivot();

      if (!data || data.length === 0) {
        toast({
          title: "No data to export",
          description: "This widget has no data to export",
          variant: "destructive",
        });
        return;
      }

      // Export to CSV
      const filename = `${widget.title || 'widget-data'}-${format(new Date(), 'yyyy-MM-dd-HHmmss')}`;
      exportToCSV(data, filename);

      toast({
        title: "Export successful",
        description: `${widget.title || 'Widget data'} has been exported to CSV`,
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        title: "Export failed",
        description: "Failed to export widget data",
        variant: "destructive",
      });
    }
  };

  const handleLayoutChange = (layout: any[]) => {
    // Only update if layout has actually changed
    const hasChanged = layout.some(layoutItem => {
      const widget = config.widgets.find(w => w.i === layoutItem.i);
      return widget && (
        widget.x !== layoutItem.x ||
        widget.y !== layoutItem.y ||
        widget.w !== layoutItem.w ||
        widget.h !== layoutItem.h
      );
    });

    if (!hasChanged) return;

    console.log('[DashboardBuilder] Layout changed, updating positions');

    const updatedWidgets = config.widgets.map(widget => {
      const layoutItem = layout.find(l => l.i === widget.i);
      if (layoutItem) {
        return {
          ...widget,
          x: layoutItem.x,
          y: layoutItem.y,
          w: layoutItem.w,
          h: layoutItem.h,
        };
      }
      return widget;
    });
    setConfig(prev => ({ ...prev, widgets: updatedWidgets }));

    // Debounced auto-save (1 second delay)
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveWidgetPositions(updatedWidgets);
    }, 1000);
  };

  const toggleMeasure = (measure: string) => {
    if (!editingWidget) return;

    const isAdding = !editingWidget.measures.includes(measure);
    const isRemoving = editingWidget.measures.includes(measure);

    if (isRemoving) {
      setEditingWidget({
        ...editingWidget,
        measures: editingWidget.measures.filter((m) => m !== measure),
      });
      return;
    }

    if (isAdding && editingWidget.measures.length > 0) {
      const existingCube = editingWidget.measures[0].split('.')[0];
      const newCube = measure.split('.')[0];

      if (existingCube !== newCube) {
        toast({
          title: "Cannot mix measure cubes",
          description: `All measures must be from the same cube. Currently using: ${existingCube}`,
          variant: "destructive",
        });
        return;
      }
    }

    // Auto-select time dimension based on cube
    let timeDimension = editingWidget.timeDimension;
    const cubeName = measure.split('.')[0];

    // If no time dimension is set yet, auto-select the createdAt dimension for this cube
    if (!timeDimension && meta?.cubes) {
      const cube = meta.cubes.find((c) => c.name === cubeName);
      if (cube) {
        // Look for a createdAt or similar time dimension
        const timeField = cube.dimensions.find((d) =>
          d.type === 'time' &&
          (d.name.toLowerCase().includes('createdat') ||
           d.name.toLowerCase().includes('created_at') ||
           d.name.toLowerCase().includes('created'))
        );

        if (timeField) {
          timeDimension = {
            dimension: timeField.name,
            granularity: 'month'
          };
        }
      }
    }

    setEditingWidget({
      ...editingWidget,
      measures: [...editingWidget.measures, measure],
      timeDimension: timeDimension,
    });
  };

  const toggleDimension = (dimension: string) => {
    if (!editingWidget) return;

    setEditingWidget({
      ...editingWidget,
      dimensions: editingWidget.dimensions.includes(dimension)
        ? editingWidget.dimensions.filter((d) => d !== dimension)
        : [...editingWidget.dimensions, dimension],
    });
  };

  const setTimeDimension = (dimension: string) => {
    if (!editingWidget) return;

    setEditingWidget({
      ...editingWidget,
      timeDimension: {
        dimension,
        granularity: editingWidget.timeDimension?.granularity || "month",
        dateRange: editingWidget.timeDimension?.dateRange || "Last 30 days",
      },
    });
  };

  const clearTimeDimension = () => {
    if (!editingWidget) return;

    setEditingWidget({
      ...editingWidget,
      timeDimension: undefined,
    });
  };

  const saveDashboardMetadata = async (silent = false, overrideSettings?: {
    preset?: string;
    startDate?: Date;
    endDate?: Date;
  }) => {
    if (!config.id) {
      console.log('[saveDashboardMetadata] Skipped - no config.id');
      return;
    }

    try {
      // Use override settings if provided, otherwise use state
      const preset = overrideSettings?.preset ?? selectedPreset;
      const startDate = overrideSettings?.startDate ?? dashboardStartDate;
      const endDate = overrideSettings?.endDate ?? dashboardEndDate;

      // Prepare date range settings
      const dateRangeSettings = {
        type: preset === 'custom' ? 'custom' : 'preset',
        preset: preset,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      };

      console.log('[saveDashboardMetadata] Saving dashboard metadata:', {
        id: config.id,
        name: config.name,
        description: config.description,
        dateRangeSettings,
        usingOverride: !!overrideSettings,
        silent
      });

      const { error, data } = await supabase
        .from('analytics_dashboards')
        .update({
          name: config.name,
          description: config.description,
          date_range_settings: dateRangeSettings,
        })
        .eq('id', config.id)
        .select();

      if (error) {
        console.error("[saveDashboardMetadata] Dashboard update error:", error);
        if (!silent) {
          toast({
            title: "Error",
            description: `Failed to update dashboard: ${error.message}`,
            variant: "destructive",
          });
        }
        return;
      }

      console.log('[saveDashboardMetadata] Dashboard updated successfully:', data);

      if (!silent) {
        toast({
          title: "Success",
          description: "Dashboard updated successfully",
        });
      }
    } catch (error) {
      console.error("[saveDashboardMetadata] Unexpected error updating dashboard:", error);
      if (!silent) {
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive",
        });
      }
    }
  };

  if (metaLoading || dashboardLoading) {
    return (
      <BackofficeLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="ml-4 text-muted-foreground">
            {dashboardLoading ? "Loading dashboard..." : "Loading metadata..."}
          </p>
        </div>
      </BackofficeLayout>
    );
  }

  if (error) {
    return (
      <BackofficeLayout>
        <div className="flex flex-col items-center justify-center h-96 space-y-4">
          <p className="text-lg font-semibold text-red-600">Metadata Error</p>
          <p className="text-muted-foreground">{error.toString()}</p>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </div>
      </BackofficeLayout>
    );
  }

  if (!meta || !meta.cubes || meta.cubes.length === 0) {
    return (
      <BackofficeLayout>
        <div className="flex flex-col items-center justify-center h-96 space-y-4">
          <p className="text-lg font-semibold">No metadata found</p>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate("/backoffice/analytics")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={config.name}
                    onChange={(e) => setConfig(prev => ({ ...prev, name: e.target.value }))}
                    className="text-2xl font-bold h-10"
                    autoFocus
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        await saveDashboardMetadata();
                        setIsEditingName(false);
                      }
                      if (e.key === 'Escape') setIsEditingName(false);
                    }}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={async (e) => {
                      e.preventDefault();
                      await saveDashboardMetadata();
                      setIsEditingName(false);
                    }}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div
                  className="flex items-center gap-2 cursor-pointer group hover:bg-muted/50 rounded px-2 -mx-2"
                  onClick={() => setIsEditingName(true)}
                >
                  <h1 className="text-3xl font-bold tracking-tight">
                    {config.name}
                  </h1>
                  <Edit2 className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              )}

              {isEditingDescription ? (
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={config.description}
                    onChange={(e) => setConfig(prev => ({ ...prev, description: e.target.value }))}
                    className="h-8 text-sm"
                    placeholder="Add a description..."
                    autoFocus
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        await saveDashboardMetadata();
                        setIsEditingDescription(false);
                      }
                      if (e.key === 'Escape') setIsEditingDescription(false);
                    }}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={async (e) => {
                      e.preventDefault();
                      await saveDashboardMetadata();
                      setIsEditingDescription(false);
                    }}
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <div
                  className="cursor-pointer group hover:bg-muted/50 rounded px-2 -mx-2"
                  onClick={() => setIsEditingDescription(true)}
                >
                  <p className="text-muted-foreground flex items-center gap-2">
                    {config.description || "Click to add a description"}
                    <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Date Range Picker */}
        <div className="flex items-center gap-4">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="min-w-[280px] justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dashboardStartDate && dashboardEndDate ? (
                  <>
                    {format(dashboardStartDate, "MMM d, yyyy")} - {format(dashboardEndDate, "MMM d, yyyy")}
                  </>
                ) : (
                  <span>Select date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" side="bottom">
              <div className="flex">
                {/* Presets sidebar */}
                <div className="border-r p-2 space-y-0.5 w-[140px]">
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "last_30_days" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => applyDatePreset("last_30_days")}
                  >
                    Last 30 days
                  </Button>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "last_60_days" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => applyDatePreset("last_60_days")}
                  >
                    Last 60 days
                  </Button>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "last_90_days" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => applyDatePreset("last_90_days")}
                  >
                    Last 90 days
                  </Button>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "last_180_days" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => applyDatePreset("last_180_days")}
                  >
                    Last 180 days
                  </Button>
                  <div className="border-t my-1"></div>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "this_year" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => applyDatePreset("this_year")}
                  >
                    This year
                  </Button>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "last_year" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => applyDatePreset("last_year")}
                  >
                    Last year
                  </Button>
                  <div className="border-t my-1"></div>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "custom" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => applyDatePreset("custom")}
                  >
                    Custom
                  </Button>
                </div>

                {/* Calendar section - always visible */}
                <div className="p-3">
                  <div className="flex gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground mb-2 block">From</Label>
                      <Calendar
                        key={`from-${dashboardStartDate?.getTime()}`}
                        mode="single"
                        selected={dashboardStartDate}
                        month={dashboardStartDate}
                        onSelect={(date) => {
                          setDashboardStartDate(date);
                          setSelectedPreset("custom");
                        }}
                        initialFocus
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground mb-2 block">To</Label>
                      <Calendar
                        key={`to-${dashboardEndDate?.getTime()}`}
                        mode="single"
                        selected={dashboardEndDate}
                        month={dashboardEndDate}
                        onSelect={(date) => {
                          setDashboardEndDate(date);
                          setSelectedPreset("custom");
                        }}
                        disabled={(date) =>
                          dashboardStartDate ? date < dashboardStartDate : false
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Dashboard Grid */}
        <div className="min-h-[600px] border rounded-lg p-4 bg-muted/20">
          {config.widgets.length === 0 ? (
            <div className="flex items-center justify-center h-96">
              <Card className="w-96 cursor-pointer hover:border-primary transition-colors" onClick={handleAddWidget}>
                <CardContent className="flex flex-col items-center justify-center h-48 text-center">
                  <Plus className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Add Your First Widget</h3>
                  <p className="text-sm text-muted-foreground">
                    Click here to create a chart or table widget
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : (
            <>
              <ResponsiveGridLayout
                className="layout"
                layout={layout}
                cols={24}
                rowHeight={60}
                onLayoutChange={handleLayoutChange}
                draggableHandle=".drag-handle"
                isResizable={true}
                resizeHandles={['se', 'sw', 'ne', 'nw', 's', 'e', 'w', 'n']}
                compactType={null}
                preventCollision={false}
              >
                {config.widgets.map((widget) => (
                  <div key={widget.i} className="bg-background rounded-lg border shadow-sm">
                    <Card className="h-full flex flex-col">
                      <CardHeader className="drag-handle cursor-move pb-3 flex flex-row items-center justify-between">
                        <CardTitle className="text-base">{widget.title || "Untitled Widget"}</CardTitle>
                        <div className="flex gap-1" onMouseDown={(e) => e.stopPropagation()}>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportWidget(widget);
                            }}
                            title="Export to CSV"
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditWidget(widget);
                            }}
                            title="Edit widget"
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteWidget(widget.i);
                            }}
                            title="Delete widget"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="flex-1 pt-0 pb-4 overflow-auto">
                        <div className="h-full">
                          <WidgetChart
                            widget={widget}
                            meta={meta}
                            dashboardStartDate={dashboardStartDate}
                            dashboardEndDate={dashboardEndDate}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </ResponsiveGridLayout>

              <Card className="mt-4 cursor-pointer hover:border-primary transition-colors" onClick={handleAddWidget}>
                <CardContent className="flex items-center justify-center h-24">
                  <Plus className="h-6 w-6 text-muted-foreground mr-2" />
                  <span className="text-sm text-muted-foreground">Add Widget</span>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* Widget Configuration Sheet */}
      <Sheet open={isWidgetSheetOpen} onOpenChange={setIsWidgetSheetOpen}>
        <SheetContent className="sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {editingWidget && config.widgets.find(w => w.i === editingWidget.i) ? "Edit Widget" : "Add Widget"}
            </SheetTitle>
            <SheetDescription>
              Configure your analytics widget
            </SheetDescription>
          </SheetHeader>

          {editingWidget && (
            <div className="space-y-6 py-6">
              {/* Widget Title */}
              <div className="space-y-2">
                <Label>Widget Title</Label>
                <Input
                  value={editingWidget.title}
                  onChange={(e) => setEditingWidget({ ...editingWidget, title: e.target.value })}
                  placeholder="Enter widget title"
                />
              </div>

              {/* Module Selection */}
              <div className="space-y-2">
                <Label>Module</Label>
                <p className="text-xs text-muted-foreground">
                  Select which module you want to analyze
                </p>
                <Select
                  value={editingWidget.measures.length > 0 ? editingWidget.measures[0].split('.')[0] : ""}
                  onValueChange={(cubeName) => {
                    // Find the selected cube and auto-select its first measure
                    const cube = meta?.cubes?.find(c => c.name === cubeName);
                    const firstMeasure = cube?.measures?.[0]?.name;

                    // Auto-select time dimension for this cube
                    let timeDimension = undefined;
                    if (cube) {
                      const timeField = cube.dimensions.find((d) =>
                        d.type === 'time' &&
                        (d.name.toLowerCase().includes('createdat') ||
                         d.name.toLowerCase().includes('created_at') ||
                         d.name.toLowerCase().includes('created'))
                      );
                      if (timeField) {
                        timeDimension = {
                          dimension: timeField.name,
                          granularity: 'month'
                        };
                      }
                    }

                    // Set the first measure from the selected cube
                    setEditingWidget({
                      ...editingWidget,
                      measures: firstMeasure ? [firstMeasure] : [],
                      dimensions: [],
                      filters: undefined,
                      timeDimension: timeDimension
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a module" />
                  </SelectTrigger>
                  <SelectContent>
                    {meta?.cubes?.map((cube) => (
                      <SelectItem key={cube.name} value={cube.name}>
                        {cube.title || cube.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Chart Type */}
              <div className="space-y-2">
                <Label>Chart Type</Label>
                <Select
                  value={editingWidget.chartType}
                  onValueChange={(value) =>
                    setEditingWidget({ ...editingWidget, chartType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CHART_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Measures - Only show if entity is selected */}
              {(() => {
                const selectedCubeName = editingWidget.measures.length > 0
                  ? editingWidget.measures[0].split('.')[0]
                  : null;
                const selectedCube = selectedCubeName
                  ? meta?.cubes?.find(c => c.name === selectedCubeName)
                  : null;

                if (!selectedCube) {
                  return (
                    <div className="space-y-2">
                      <Label>Measures</Label>
                      <p className="text-sm text-muted-foreground italic">
                        Please select a module first
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-2">
                    <Label>Measures</Label>
                    <p className="text-xs text-muted-foreground">
                      Select metrics to display for {selectedCube.title || selectedCube.name}
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                      {selectedCube.measures.map((measure) => (
                        <div
                          key={measure.name}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={measure.name}
                            checked={editingWidget.measures.includes(measure.name)}
                            onCheckedChange={() => toggleMeasure(measure.name)}
                          />
                          <Label
                            htmlFor={measure.name}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {measure.shortTitle || measure.title || measure.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Dimensions - Only show if entity is selected */}
              {(() => {
                const selectedCubeName = editingWidget.measures.length > 0
                  ? editingWidget.measures[0].split('.')[0]
                  : null;
                const selectedCube = selectedCubeName
                  ? meta?.cubes?.find(c => c.name === selectedCubeName)
                  : null;

                if (!selectedCube) {
                  return (
                    <div className="space-y-2">
                      <Label>Dimensions (Optional)</Label>
                      <p className="text-sm text-muted-foreground italic">
                        Please select a module first
                      </p>
                    </div>
                  );
                }

                const nonTimeDimensions = selectedCube.dimensions.filter((dim) => dim.type !== "time");

                return (
                  <div className="space-y-2">
                    <Label>Dimensions (Optional)</Label>
                    <p className="text-xs text-muted-foreground">
                      Break down data by {selectedCube.title || selectedCube.name} attributes
                    </p>
                    {nonTimeDimensions.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                        {nonTimeDimensions.map((dimension) => (
                          <div
                            key={dimension.name}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={dimension.name}
                              checked={editingWidget.dimensions.includes(dimension.name)}
                              onCheckedChange={() =>
                                toggleDimension(dimension.name)
                              }
                            />
                            <Label
                              htmlFor={dimension.name}
                              className="text-sm font-normal cursor-pointer"
                            >
                              {dimension.shortTitle || dimension.title || dimension.name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">
                        No dimensions available for this module
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Time Granularity - Show if module is selected */}
              {(() => {
                const selectedCubeName = editingWidget.measures.length > 0
                  ? editingWidget.measures[0].split('.')[0]
                  : null;

                if (!selectedCubeName) {
                  return (
                    <div className="space-y-2">
                      <Label>Time Granularity</Label>
                      <p className="text-sm text-muted-foreground italic">
                        Please select a module first
                      </p>
                    </div>
                  );
                }

                // Ensure time dimension exists with default granularity
                const currentGranularity = editingWidget.timeDimension?.granularity || 'month';

                return (
                  <div className="space-y-2">
                    <Label>Time Granularity</Label>
                    <p className="text-xs text-muted-foreground">
                      How to group data over time
                    </p>
                    <Select
                      value={currentGranularity}
                      onValueChange={(value) =>
                        setEditingWidget({
                          ...editingWidget,
                          timeDimension: editingWidget.timeDimension
                            ? { ...editingWidget.timeDimension, granularity: value }
                            : undefined,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_GRANULARITIES.map((gran) => (
                          <SelectItem key={gran.value} value={gran.value}>
                            {gran.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })()}

              {/* Filters */}
              <div className="space-y-2">
                <Label>Filters (Optional)</Label>
                <p className="text-xs text-muted-foreground">
                  Filter data by dimension values (e.g., status = 'approved')
                </p>
                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newFilters = editingWidget.filters || [];
                      setEditingWidget({
                        ...editingWidget,
                        filters: [
                          ...newFilters,
                          { member: '', operator: 'equals', values: [''] }
                        ]
                      });
                    }}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Filter
                  </Button>

                  {editingWidget.filters && editingWidget.filters.length > 0 && (
                    <div className="space-y-3 border rounded-md p-3 max-h-64 overflow-y-auto">
                      {editingWidget.filters.map((filter, index) => (
                        <div key={index} className="space-y-2 pb-3 border-b last:border-b-0 last:pb-0">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">Filter {index + 1}</Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newFilters = editingWidget.filters?.filter((_, i) => i !== index) || [];
                                setEditingWidget({
                                  ...editingWidget,
                                  filters: newFilters.length > 0 ? newFilters : undefined
                                });
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          <Select
                            value={filter.member}
                            onValueChange={(value) => {
                              const newFilters = [...(editingWidget.filters || [])];
                              newFilters[index] = { ...filter, member: value };
                              setEditingWidget({ ...editingWidget, filters: newFilters });
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select dimension" />
                            </SelectTrigger>
                            <SelectContent>
                              {meta?.cubes?.map((cube) =>
                                cube.dimensions
                                  .filter((dim) => dim.type !== "time")
                                  .map((dimension) => (
                                    <SelectItem key={dimension.name} value={dimension.name}>
                                      {dimension.title || dimension.name}
                                    </SelectItem>
                                  ))
                              )}
                            </SelectContent>
                          </Select>

                          <Select
                            value={filter.operator}
                            onValueChange={(value) => {
                              const newFilters = [...(editingWidget.filters || [])];
                              newFilters[index] = { ...filter, operator: value };
                              setEditingWidget({ ...editingWidget, filters: newFilters });
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equals">Equals</SelectItem>
                              <SelectItem value="notEquals">Not Equals</SelectItem>
                              <SelectItem value="contains">Contains</SelectItem>
                              <SelectItem value="notContains">Not Contains</SelectItem>
                              <SelectItem value="startsWith">Starts With</SelectItem>
                              <SelectItem value="endsWith">Ends With</SelectItem>
                              <SelectItem value="set">Is Set</SelectItem>
                              <SelectItem value="notSet">Is Not Set</SelectItem>
                            </SelectContent>
                          </Select>

                          {filter.operator !== 'set' && filter.operator !== 'notSet' && (
                            <Input
                              placeholder="Filter value (e.g., approved)"
                              value={filter.values[0] || ''}
                              onChange={(e) => {
                                const newFilters = [...(editingWidget.filters || [])];
                                newFilters[index] = { ...filter, values: [e.target.value] };
                                setEditingWidget({ ...editingWidget, filters: newFilters });
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>


              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <Button onClick={handleSaveWidget} className="flex-1">
                  {editingWidget && config.widgets.find(w => w.i === editingWidget.i) ? "Update Widget" : "Add Widget"}
                </Button>
                <Button variant="outline" onClick={() => setIsWidgetSheetOpen(false)}>
                  Cancel
                </Button>
                {editingWidget && config.widgets.find(w => w.i === editingWidget.i) && (
                  <Button
                    variant="destructive"
                    onClick={() => handleDeleteWidget(editingWidget.i)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </BackofficeLayout>
  );
};

const DashboardBuilder = () => {
  console.log('[DashboardBuilder] Main component rendering');

  return (
    <CubeProvider cubeApi={cubeApi}>
      <DashboardBuilderContent />
    </CubeProvider>
  );
};

export default DashboardBuilder;
